import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { firstTemplateCustomFieldValidationError, normalizeDocumentTitle } from 'libs.consent.common';
import { Badge, Button, Icon, InlineNotification, useDialog, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { CarryForwardReview } from './carry_forward_review.component';
import { ConsentDraftDetailsStep } from './consent_draft_details_step.component';
import { ConsentDraftRolesSitesStep, } from './consent_draft_roles_sites_step.component';
import { useConsentAutosave } from '../hooks/use_consent_autosave.hook';
import { useConsentActionsContext } from '../hooks/use_consent_context.hook';
import { useConsentSiteAutosave } from '../hooks/use_consent_site_autosave.hook';
import { ConsentPdfEditorContent } from './pdf_editor/consent_pdf_editor.component';
import { isCarryForwardBlockingPublish, isPtsOnlyConsentDocument, publishFailureSubtitle, revealPublishValidationTarget, TEMPLATE_DATA_DAMAGED_MESSAGE, validateStoredConsentDocumentForPublish, } from '../utils/consent_publish_validation';
import { isDuplicateConsentTemplateNameError } from '../utils/library_error';
const DraftWizardStepper = ({ currentStep, totalSteps, label, dataTestId, }) => {
    const text = label(currentStep);
    return (_jsxs("div", { className: 'flex items-center justify-center gap-6', role: 'group', "aria-label": text, "data-testid": dataTestId, children: [_jsx("span", { className: 'whitespace-nowrap text-sm font-medium tabular-nums text-neutral-900', children: text }), _jsx("div", { className: 'flex items-center gap-1.5', "aria-hidden": 'true', children: Array.from({ length: totalSteps }).map((_, index) => {
                    const isCurrent = index === currentStep - 1;
                    const isComplete = index < currentStep - 1;
                    return (_jsx("span", { className: 'flex h-6 w-6 items-center justify-center', children: isCurrent ? (_jsx("span", { className: 'flex h-5 w-5 items-center justify-center rounded-full bg-primary-200', children: _jsx("span", { className: 'h-2 w-2 rounded-full bg-primary-500' }) })) : (_jsx("span", { className: `h-2 w-2 rounded-full ${isComplete ? 'bg-primary-500' : 'bg-neutral-300'}` })) }, index));
                }) })] }));
};
const ASSIGNED_SITE_COUNT_TIMEOUT_MS = 10000;
/**
 * Resolves to `null` when the count cannot be read within the deadline. A count we could not read
 * must not render as "no sites are assigned" — that would understate what the publish affects.
 *
 * The deadline is not redundant with the request layer: the shared API client is created without
 * one, so a MOM that hangs rather than fails would hold the dialog closed and leave `isPublishing`
 * — and the autosave suspension it drives — on for the life of the page.
 */
const readAssignedSiteCount = async (read) => {
    let deadlineTimer;
    const deadline = new Promise((resolve) => {
        deadlineTimer = setTimeout(() => resolve(null), ASSIGNED_SITE_COUNT_TIMEOUT_MS);
    });
    try {
        return await Promise.race([read.catch(() => null), deadline]);
    }
    finally {
        clearTimeout(deadlineTimer);
    }
};
const SETUP_STEPS = ['details', 'roles', 'document'];
// A 409 rolls the field back to the last saved title but keeps the error message up, so only the
// rejected title itself should hold Publish back.
const isRejectedTitle = (error, document) => error?.documentId === document.id && error.rejectedTitle === normalizeDocumentTitle(document.name);
export const ConsentEditorModal = ({ isOpen, document, consentFiles, loadAssignedSiteCount, onClose, canEditTemplate, siteAssignment, isDocGenEconsentEnabled = false, isNewTemplate = false, }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const dialog = useDialog();
    const { loadDocuments, saveConsentTemplate, saveDocumentToStorage, publishConsentTemplate, setSelectedElement, trackTemplatePublish, } = useConsentActionsContext();
    const [view, setView] = useState('document');
    const [isSettingUp, setIsSettingUp] = useState(false);
    const [draftSiteSelection, setDraftSiteSelection] = useState({});
    const draftSiteSelectionRef = useRef(draftSiteSelection);
    draftSiteSelectionRef.current = draftSiteSelection;
    useEffect(() => {
        if (isOpen) {
            setView(isNewTemplate ? 'details' : 'document');
            setIsSettingUp(isNewTemplate);
            setDraftSiteSelection({});
        }
    }, [isOpen, document?.id, isNewTemplate]);
    // Keyed to the template so a publish left running after Close does not lock the next one opened.
    const [publishingDocumentId, setPublishingDocumentId] = useState(null);
    const isPublishing = publishingDocumentId !== null && publishingDocumentId === document?.id;
    const shownDocumentIdRef = useRef(document?.id);
    shownDocumentIdRef.current = isOpen ? document?.id : undefined;
    const [isSaving, setIsSaving] = useState(false);
    const [titleFieldError, setTitleFieldError] = useState();
    // Mirrors the state so handlePublish can see a 409 that landed while it was awaiting saves;
    // the state it captured at click time is stale by then.
    const titleFieldErrorRef = useRef();
    const updateTitleFieldError = useCallback((next) => {
        titleFieldErrorRef.current = next;
        setTitleFieldError(next);
    }, []);
    // The document holds only the roles Studio could read, so every write would replace the damaged
    // record, and with it the bundler's verdict and the evidence support needs. Only Publish stays,
    // to say why it cannot.
    const isDamaged = Boolean(document?.storedDataInvalid);
    const activeView = isDamaged ? 'document' : view;
    const isSettingUpDraft = isSettingUp && !isDamaged;
    const isTitleFieldVisible = activeView === 'details';
    useEffect(() => {
        updateTitleFieldError(undefined);
    }, [document?.id, updateTitleFieldError]);
    // The explicit Save button was easy to miss (bottom-left placement) and cost real work when
    // forgotten, so edits persist on their own. Losing a save now has no visible signal, which is
    // why a failure has to surface as a notification rather than resolve quietly — except a duplicate
    // title, which belongs on the title field.
    const handleAutosaveError = useCallback((error, failedDocument) => {
        if (isDuplicateConsentTemplateNameError(error)) {
            const message = translate('That title is already in use by another consent form');
            const failedId = failedDocument?.id ?? document?.id;
            if (failedId) {
                const rejectedTitle = normalizeDocumentTitle((failedDocument ?? document)?.name ?? '');
                updateTitleFieldError({ documentId: failedId, message, rejectedTitle });
            }
            // The title Input lives only on the details step. Roles/Sites, placements, or another
            // template means the field is gone — keep a toast so the 409 is visible.
            const titlePanelMounted = Boolean(failedId) && document?.id === failedId && isTitleFieldVisible;
            if (!titlePanelMounted) {
                addNotification({
                    type: 'error',
                    title: message,
                });
            }
            return;
        }
        addNotification({
            type: 'error',
            title: translate('Changes not saved'),
            subtitle: translate('Your latest changes could not be saved. Keep editing to try again.'),
        });
    }, [addNotification, document, isTitleFieldVisible, updateTitleFieldError, translate]);
    const { settlePendingSaves } = useConsentAutosave({
        document,
        isSuspended: isPublishing || isSaving || isDamaged,
        save: saveDocumentToStorage,
        onSaveError: handleAutosaveError,
    });
    const reportSiteSaveError = useCallback(() => {
        addNotification({
            type: 'error',
            title: translate('Site assignment was not stored'),
            subtitle: translate('Your latest site selections could not be saved. Keep editing to try again.'),
        });
    }, [addNotification, translate]);
    const { flush: flushSiteAssignments } = useConsentSiteAutosave({
        templateKey: document?.key,
        pendingByEnv: draftSiteSelection,
        sites: siteAssignment?.sites,
        // Assignments are by template key, so a damaged draft would also move its published sibling's sites.
        saveAssignments: isDamaged ? undefined : siteAssignment?.saveAssignments,
        isSuspended: isPublishing || isSaving || isDamaged,
        onError: reportSiteSaveError,
    });
    const handlePublish = useCallback(async () => {
        if (!document || isPublishing) {
            return;
        }
        const validationError = validateStoredConsentDocumentForPublish(document);
        if (validationError) {
            addNotification({
                type: 'error',
                title: translate('Publish failed'),
                subtitle: translate(validationError.message, validationError.params),
            });
            if (validationError.elementId) {
                setSelectedElement(validationError.elementId);
            }
            requestAnimationFrame(() => revealPublishValidationTarget(validationError));
            return;
        }
        // A template with roles but no digital fields is valid for a Print-to-Sign (PTS)-only
        // workflow — surface this as a heads-up, not a block, since it's easy to publish this way by
        // mistake if the author actually intended eSign.
        if (isPtsOnlyConsentDocument(document)) {
            addNotification({
                type: 'warning',
                title: translate('Publishing without digital signature fields'),
                subtitle: translate('This template has no digital signature fields configured. It will only be usable for Print-to-Sign (PTS) consent forms.'),
            });
        }
        // Held from here rather than only around the publish calls, because the site-count read below
        // is awaited before the dialog opens — without it a second click would start a second read and
        // stack a second dialog.
        setPublishingDocumentId(document.id);
        try {
            const assignedSiteCount = loadAssignedSiteCount
                ? await readAssignedSiteCount(loadAssignedSiteCount(document.id, draftSiteSelectionRef.current))
                : null;
            const siteAssignmentSentence = assignedSiteCount === null
                ? translate("Once published, sites can be assigned but the contents can't be edited.")
                : assignedSiteCount === 0
                    ? translate("No sites are assigned yet. Once published, sites can be assigned but the contents can't be edited.")
                    : assignedSiteCount === 1
                        ? translate("This template is assigned to 1 site. Once published, additional sites can be assigned but the contents can't be edited.")
                        : translate("This template is assigned to {siteCount} sites. Once published, additional sites can be assigned but the contents can't be edited.", { siteCount: String(assignedSiteCount) });
            // Publishing auto-archives the current Published sibling in the same lineage, so the dialog
            // must say so and name the version being archived when a sibling actually exists, not just
            // describe the generic publish consequence. Falls back to no extra sentence on a template's
            // very first publish (no Published sibling yet), where nothing will be archived.
            const publishedSibling = consentFiles.find((file) => file.templateKey !== undefined && file.templateKey === document.key && file.status === 'Published');
            const archiveWarning = publishedSibling?.version !== undefined
                ? translate("Version {version} will be archived and can't be recovered.", {
                    version: String(publishedSibling.version),
                })
                : null;
            const confirmed = await dialog.open({
                title: translate('Publish this template?'),
                body: (_jsxs(_Fragment, { children: [_jsx("p", { children: siteAssignmentSentence }), archiveWarning ? _jsx("p", { children: archiveWarning }) : null] })),
                actionButtons: [
                    { label: translate('Publish'), dataTestId: 'consent-publish-confirm' },
                    { label: translate('Cancel'), dataTestId: 'consent-publish-cancel' },
                ],
                willCloseOnOutsideClickAndEscapeKeyPress: false,
            });
            if (!confirmed) {
                return;
            }
            // The author may close the editor, or open another template, while this runs. Captured now,
            // because opening another template resets the draft selection.
            const sitePicks = draftSiteSelectionRef.current;
            const isStillShown = () => shownDocumentIdRef.current === document.id;
            await trackTemplatePublish(document.id, async () => {
                try {
                    // `saveConsentTemplate` persists the same fields autosave does before it bundles, so the
                    // debounced save is dropped rather than duplicated — but any save already dispatched has
                    // to settle first, or it could land after the bundle was compiled.
                    await settlePendingSaves();
                    // A rename 409 from that save drops the title from the next save, so publishing here
                    // would keep the old title without saying so.
                    if (isDocGenEconsentEnabled && isRejectedTitle(titleFieldErrorRef.current, document)) {
                        if (isStillShown()) {
                            setView('details');
                        }
                        return;
                    }
                    await saveConsentTemplate(document);
                    await publishConsentTemplate(document);
                    const sitesOk = await flushSiteAssignments(sitePicks);
                    if (!sitesOk) {
                        addNotification({
                            type: 'error',
                            title: translate('Site assignment was not stored'),
                            subtitle: translate('The template was published. Assign sites from the published template.'),
                        });
                    }
                    if (isStillShown()) {
                        onClose();
                    }
                    await loadDocuments();
                }
                catch (error) {
                    if (isDuplicateConsentTemplateNameError(error) && isStillShown()) {
                        setView('details');
                        handleAutosaveError(error, document);
                        return;
                    }
                    addNotification({
                        type: 'error',
                        title: translate('Publish failed'),
                        subtitle: publishFailureSubtitle(error, translate),
                    });
                }
            });
        }
        finally {
            setPublishingDocumentId((current) => (current === document.id ? null : current));
        }
    }, [
        document,
        isPublishing,
        consentFiles,
        dialog,
        loadAssignedSiteCount,
        settlePendingSaves,
        saveConsentTemplate,
        publishConsentTemplate,
        trackTemplatePublish,
        flushSiteAssignments,
        onClose,
        loadDocuments,
        addNotification,
        translate,
        setSelectedElement,
        handleAutosaveError,
        isDocGenEconsentEnabled,
    ]);
    const handleClose = useCallback(() => {
        if (!document) {
            updateTitleFieldError(undefined);
            onClose();
            return;
        }
        // A damaged draft cannot be edited, so a stored custom-field error must not trap the author in it.
        const customFieldError = isDamaged ? null : firstTemplateCustomFieldValidationError(document.customFields);
        if (customFieldError) {
            addNotification({
                type: 'error',
                title: translate('Cannot close'),
                subtitle: translate(customFieldError.message, customFieldError.params),
            });
            return;
        }
        updateTitleFieldError(undefined);
        onClose();
    }, [document, isDamaged, onClose, addNotification, updateTitleFieldError, translate]);
    const handleSave = useCallback(async () => {
        if (!document || isSaving || isDamaged) {
            return;
        }
        setIsSaving(true);
        try {
            // Autosave drops its debounced write here rather than flushing it, so write the latest
            // state ourselves once anything already in flight has landed.
            await settlePendingSaves();
            await saveDocumentToStorage(document);
            const sitesOk = await flushSiteAssignments();
            if (!sitesOk) {
                return;
            }
        }
        catch (error) {
            handleAutosaveError(error, document);
            if (isDuplicateConsentTemplateNameError(error)) {
                setView('details');
            }
            return;
        }
        finally {
            setIsSaving(false);
        }
        const savedSites = Object.keys(draftSiteSelectionRef.current).length > 0;
        addNotification({
            type: 'success',
            title: translate('Draft saved'),
            ...(savedSites
                ? {
                    subtitle: translate('Site selections also update the published version of this template.'),
                }
                : {}),
        });
        handleClose();
    }, [
        document,
        isSaving,
        isDamaged,
        settlePendingSaves,
        saveDocumentToStorage,
        flushSiteAssignments,
        handleAutosaveError,
        addNotification,
        translate,
        handleClose,
    ]);
    const placementsBlockPublish = Boolean(document && isCarryForwardBlockingPublish(document));
    // Saves skip a rejected or blank title, so publishing now would silently keep the old one.
    const titleBlocksPublish = Boolean(isDocGenEconsentEnabled &&
        document &&
        (isRejectedTitle(titleFieldError, document) || !normalizeDocumentTitle(document.name)));
    const setupStepIndex = SETUP_STEPS.indexOf(activeView);
    const setupContinue = {
        label: translate('Continue'),
        onClick: () => {
            const next = SETUP_STEPS[setupStepIndex + 1];
            setView(next);
            if (next === 'document') {
                setIsSettingUp(false);
            }
        },
        disabled: activeView === 'details' && titleBlocksPublish,
        dataTestId: 'draft-wizard-continue',
    };
    const editorViews = [
        { view: 'document', label: translate('Document') },
        { view: 'details', label: translate('Details') },
        { view: 'roles', label: translate('Roles & sites') },
    ];
    if (!isOpen) {
        return null;
    }
    return (_jsxs("div", { className: 'flex min-h-0 flex-1 flex-col overflow-hidden bg-background', "data-testid": 'consent-editor-modal', children: [_jsxs("header", { className: 'flex shrink-0 flex-col gap-1 border-0 border-b border-solid border-neutral-200 pb-3', children: [_jsxs("div", { className: 'flex min-w-0 items-center gap-2', children: [_jsxs("nav", { className: 'flex shrink-0 items-center gap-2', "aria-label": translate('Breadcrumbs'), children: [_jsx("button", { type: 'button', className: 'border-0 bg-transparent p-0 text-sm font-medium text-primary-500', onClick: handleClose, "data-testid": 'editor-breadcrumb-consent', children: translate('Consent') }), _jsx(Icon, { name: 'chevronRight', size: 'xs' })] }), _jsx("h1", { className: 'm-0 truncate text-lg font-semibold leading-7 text-neutral-900', "data-testid": 'modal-document-title', children: document?.name ?? translate('Template') }), document ? _jsx(Badge, { variant: 'warning', label: translate('Draft') }) : null, document && !isSettingUp && !isDamaged ? (_jsx("nav", { className: 'ml-auto flex shrink-0 items-center gap-1', "aria-label": translate('Template sections'), children: editorViews.map((item) => (_jsx("button", { type: 'button', "aria-current": activeView === item.view ? 'page' : undefined, className: `rounded-md border-0 px-2.5 py-1 text-sm font-medium ${activeView === item.view
                                        ? 'bg-primary-100 text-primary-600'
                                        : 'bg-transparent text-neutral-600 hover:bg-neutral-100'}`, onClick: () => setView(item.view), "data-testid": `editor-view-${item.view}`, children: item.label }, item.view))) })) : null] }), document && isSettingUpDraft ? (_jsx("p", { className: 'm-0 text-sm font-medium text-neutral-900/60', children: activeView === 'details' ? translate('Template details') : translate('Roles & Sites') })) : null] }), _jsx("div", { className: 'flex min-h-0 flex-1 flex-row overflow-hidden', "data-testid": 'consent-editor-modal-body', children: _jsx("div", { className: 'flex min-h-0 flex-1 flex-col overflow-hidden', children: !document ? (_jsx("div", { className: 'flex flex-1 items-center justify-center py-12 text-sm text-neutral-500', children: translate('Select a template to edit.') })) : isDamaged ? (_jsxs(_Fragment, { children: [_jsx("div", { className: 'px-4 py-3', "data-testid": 'editor-stored-data-invalid', children: _jsx(InlineNotification, { type: 'error', title: translate(TEMPLATE_DATA_DAMAGED_MESSAGE) }) }), _jsx(ConsentPdfEditorContent, { document: document, readOnly: true })] })) : activeView === 'document' ? (_jsxs(_Fragment, { children: [_jsx(CarryForwardReview, { document: document }), _jsx(ConsentPdfEditorContent, { document: document })] })) : activeView === 'details' ? (_jsx(ConsentDraftDetailsStep, { document: document, titleError: titleFieldError?.documentId === document.id ? titleFieldError.message : undefined, onTitleFieldErrorClear: () => updateTitleFieldError(undefined) })) : (_jsx(ConsentDraftRolesSitesStep, { document: document, siteAssignment: siteAssignment
                            ? {
                                ...siteAssignment,
                                pendingSelectionByEnv: draftSiteSelection,
                                onSelectionChange: (environmentId, selectedIds) => {
                                    setDraftSiteSelection((current) => ({ ...current, [environmentId]: selectedIds }));
                                },
                            }
                            : undefined })) }) }), _jsx("footer", { className: 'relative flex shrink-0 items-center justify-end gap-3 border-0 border-t border-solid border-neutral-200 px-4 py-2', children: isSettingUpDraft ? (_jsxs(_Fragment, { children: [setupStepIndex > 0 ? (_jsx("div", { className: 'mr-auto', children: _jsx(Button, { variant: 'secondary', label: translate('Back'), onClick: () => setView(SETUP_STEPS[setupStepIndex - 1]) }) })) : null, _jsx("div", { className: 'absolute left-1/2 -translate-x-1/2', children: _jsx(DraftWizardStepper, { currentStep: setupStepIndex + 1, totalSteps: SETUP_STEPS.length, dataTestId: 'draft-wizard-step', label: (currentStep) => translate('Step {step} of 3', { step: String(currentStep) }) }) }), _jsx(Button, { variant: 'secondary', label: translate('Cancel'), onClick: handleClose }), canEditTemplate ? _jsx(Button, { ...setupContinue }) : null] })) : (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'secondary', label: translate('Cancel'), onClick: handleClose }), canEditTemplate ? (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'secondary', label: translate('Save'), onClick: () => void handleSave(), 
                                    // Saves skip a rejected or blank title, so saving now would close with the old one.
                                    disabled: isSaving || isPublishing || titleBlocksPublish || isDamaged, dataTestId: 'consent-editor-save' }), _jsx(Button, { label: translate('Publish'), onClick: () => void handlePublish(), 
                                    // A damaged draft cannot be fixed here, so Publish stays clickable to say why it is refused.
                                    disabled: isSaving || isPublishing || (!isDamaged && (placementsBlockPublish || titleBlocksPublish)) })] })) : null] })) })] }));
};
